import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { historyApi } from '../api/historyContributions';
import { ApiError } from '../lib/api';
import { toast } from '../lib/toast';
import { Pagination } from '../components/Pagination';
import { PageSkeleton } from '../components/Skeleton';
import { ContributionStatusBadge } from '../components/ContributionStatusBadge';

/** Landing page: what this is, start one, and the member's own contributions. */
export function Contribute() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const eventId = params.get('eventId');
  const [page, setPage] = useState(1);
  const started = useRef(false);

  const create = useMutation({
    mutationFn: (linkedEventId?: string | null) => historyApi.create(linkedEventId ? { linkedEventId } : {}),
    onSuccess: (c) => navigate(`/contribute/${c.id}`, { replace: !!eventId }),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Could not start a contribution'),
  });

  // "Add to this tasting's record" arrives with ?eventId= — start pre-linked and go straight to the form.
  useEffect(() => {
    if (eventId && !started.current) {
      started.current = true;
      create.mutate(eventId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const { data, isLoading } = useQuery({
    queryKey: ['contributions', 'mine', page],
    queryFn: () => historyApi.list({ scope: 'mine', page }),
  });

  if (eventId) return <PageSkeleton lines={3} />;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-gold-400">Tasting Archive</p>
        <h1 className="mt-2 font-display text-3xl text-cream-50">Contribute to the club's history</h1>
        <p className="mt-2 max-w-2xl text-sm text-cream-300/80">
          Forty years of tastings, and a lot of it lives in shoeboxes, notebooks and memories. Whatever you have about a past
          tasting — a photo of a scorecard, an old spreadsheet, a menu, or just what you remember — send it in. Nothing is
          required. A Records Admin reviews each contribution and folds it into the archive.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button className="btn-primary" onClick={() => create.mutate(null)} disabled={create.isPending}>
            {create.isPending ? 'Starting…' : 'Start a contribution'}
          </button>
          <Link className="btn-ghost" to="/archive">
            Browse the archive first
          </Link>
        </div>
        <p className="mt-2 text-xs text-cream-300/50">
          Tip: if the tasting is already in the archive, open it there and press “Add to this tasting's record”.
        </p>
      </div>

      <section className="space-y-3">
        <h2 className="text-sm uppercase tracking-[0.15em] text-gold-400">Your contributions</h2>
        {isLoading ? (
          <PageSkeleton lines={3} />
        ) : !data || data.items.length === 0 ? (
          <p className="text-sm text-cream-300/60">Nothing yet.</p>
        ) : (
          <ul className="divide-y divide-ink-800 rounded border border-ink-800">
            {data.items.map((c) => (
              <li key={c.id}>
                <Link to={`/contribute/${c.id}`} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 hover:bg-ink-900/40">
                  <div>
                    <p className="text-cream-50">
                      {c.title ?? <span className="text-cream-300/50">Untitled</span>}
                      {c.year ? <span className="ml-2 text-cream-300/50">{c.year}</span> : null}
                    </p>
                    <p className="text-xs text-cream-300/50">
                      {c.fileCount} file{c.fileCount === 1 ? '' : 's'} · updated {new Date(c.updatedAt).toLocaleDateString()}
                      {c.reviewNotes && c.status !== 'merged' ? ` · “${c.reviewNotes}”` : ''}
                    </p>
                  </div>
                  <ContributionStatusBadge status={c.status} />
                </Link>
              </li>
            ))}
          </ul>
        )}
        {data && <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />}
      </section>
    </div>
  );
}
